import { Clock, UserCheck, UserRound, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ACCENT_MARK_CLASS } from '@/styles/accentMark';
import { formatDisplayDateTime } from '@/utils/date';

const NOT_SET_TEXT = 'Not set';
const ICON_CIRCLE_CLASS = cn(
  'flex size-6 shrink-0 items-center justify-center rounded-full',
  ACCENT_MARK_CLASS,
);

function MetaDivider() {
  return <span className="h-4 w-px bg-border max-sm:hidden" aria-hidden />;
}

interface SubmitterMetaProps {
  label: string;
  icon?: LucideIcon;
  value: string;
  className?: string;
}

function SubmitterMeta({ label, icon: Icon, value, className }: SubmitterMetaProps) {
  return (
    <span className={cn('flex items-center gap-1.5', className)}>
      {Icon && (
        <span className={cn(ICON_CIRCLE_CLASS, 'sm:hidden')}>
          <Icon className="size-3.5" aria-hidden />
        </span>
      )}
      <span className="text-xs text-muted-foreground max-sm:sr-only">{label}</span>
      <span className="font-medium text-foreground max-sm:text-xs">{value}</span>
    </span>
  );
}

interface SubmitterDetailsProps {
  name: string;
  hrEmpId: string | null;
  amName: string | null;
  submittedAt: string;
  className?: string;
}

export function SubmitterDetails({
  name,
  hrEmpId,
  amName,
  submittedAt,
  className,
}: SubmitterDetailsProps) {
  return (
    <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm', className)}>
      <span className="flex items-center gap-2 max-sm:w-full">
        <span className={cn(ICON_CIRCLE_CLASS, 'sm:size-7')}>
          <UserRound className="size-3.5 sm:size-4" aria-hidden />
        </span>
        <span className="font-semibold text-foreground">
          {name}
          {hrEmpId && (
            <span className="font-normal text-muted-foreground sm:hidden"> ({hrEmpId})</span>
          )}
        </span>
      </span>
      <MetaDivider />
      <SubmitterMeta
        label="Employee ID"
        value={hrEmpId ?? NOT_SET_TEXT}
        className="max-sm:hidden"
      />
      <MetaDivider />
      <SubmitterMeta label="Reports to" icon={UserCheck} value={amName ?? NOT_SET_TEXT} />
      <MetaDivider />
      <SubmitterMeta label="Submitted" icon={Clock} value={formatDisplayDateTime(submittedAt)} />
    </div>
  );
}
